import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { forkJoin, map, of, switchMap } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IIndicatorMap } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { CheckInSettingsDTO, CheckInTemplateDTO } from "@webapp/reflections/models/checkins-facade.models";
import { CheckInQuestion, CheckInSettings, CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { CheckInFormQuestion, CheckInQuestionControlsConfig } from "@webapp/reflections/models/manage-reflections.models";
import { ReflectionPanel } from "@webapp/reflections/models/reflection-panel.models";
import { CheckInsDataService } from "@webapp/reflections/services/checkins-data.service";
import { CheckInsFacade } from "@webapp/reflections/services/checkins-facade.service";
import { Team } from "@webapp/teams/models/teams.models";
import { TeamsFacade } from "@webapp/teams/services/teams-facade.service";

@UntilDestroy()
@Component({
  selector: "manage-reflections",
  templateUrl: "./manage-reflections.component.html",
  styleUrls: ["./manage-reflections.component.less", "./manage-reflection-questions/manage-reflection-questions.component.less"],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageReflectionsComponent implements OnInit {
  @Input()
  public teamId: string;
  public indicators: IIndicatorMap = {
    loading: { progress: true },
  };
  public formGroup = this.formBuilder.group({
    questions: this.formBuilder.array([]),
  });
  public showQuestionInput = false;
  public questions: CheckInQuestion[] = [];
  public teamReflection: ReflectionPanel;
  public team: Team;
  public isEditModeEnabled: boolean;
  public checkInTemplate: CheckInTemplate;
  public checkInSettings: CheckInSettings;
  public questionMaxLength = 200;
  public questionLength = 0;
  public newQuestionTitle: string;
  public isCheckInFormEmptyAlertEnabled: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private checkInsFacade: CheckInsFacade,
    private teamsFacade: TeamsFacade,
    private checkInsDataService: CheckInsDataService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  public ngOnInit(): void {
    const reqFilter = {
      filter: { teamId: { $in: [this.teamId] } },
    };
    const getCheckInTemplateRequest = this.checkInsFacade.getCheckInTemplate$({ teamId: this.teamId });
    const getCheckInSettingsRequest = this.checkInsFacade.getCheckInSettings$(reqFilter);
    const assigneesState = reduxStoreContainer.reduxStore.getState<IAssigneesStoreState>().assignees;
    const getTeamRequest = assigneesState.isFetched ? of(assigneesState.map[this.teamId]) : this.teamsFacade.getTeam$(this.teamId);

    forkJoin([getCheckInTemplateRequest, getCheckInSettingsRequest, getTeamRequest])
      .pipe(untilDestroyed(this))
      .subscribe(([checkInTemplateDTO, checkInSettingsDTO, team]) => {
        const { items: [checkInSettings = null] = [] } = checkInSettingsDTO;
        this.checkInTemplate = checkInTemplateDTO;
        this.checkInSettings = checkInSettings;
        this.team = team;
        this.setQuestions();
        this.buildForm();
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        delete this.indicators.loading;
        this.cdr.markForCheck();
      });
  }

  get getQuestions(): FormArray {
    return this.formGroup.controls.questions as FormArray;
  }

  public updateCounter(): void {
    this.questionLength = this.newQuestionTitle.length;
  }

  private setQuestions(): void {
    this.questions = this.checkInTemplate.questions;
  }

  private buildForm(): void {
    this.questions.forEach((question) => {
      if (question.isDefault) {
        const text = localize(`checkins_default_${question.text}`);
        this.addFormQuestion({ ...question, text, isEditModeEnabled: false });
      } else {
        this.addFormQuestion({ ...question, isEditModeEnabled: false });
      }
    });
  }

  private addFormQuestion(question: CheckInFormQuestion): void {
    const formArrayQuestions = this.formGroup.controls.questions as FormArray;
    formArrayQuestions.push(this.formBuilder.group(this.toQuestionControlsConfig(question)));
  }

  private toQuestionControlsConfig(question: CheckInFormQuestion): CheckInQuestionControlsConfig {
    return {
      id: [question.id],
      text: [question.text, [Validators.required, Validators.maxLength(200), Validators.minLength(1)]],
      isEnabled: [question.isEnabled],
      isDefault: [question.isDefault],
      isEditModeEnabled: [question.isEditModeEnabled],
    };
  }

  public updateQuestion(question: Partial<CheckInQuestion>): void {
    this.checkInsFacade
      .patchCheckInQuesion$({
        templateId: this.checkInTemplate.id,
        questionId: question.id,
        payload: {
          text: question.text,
          isEnabled: question.isEnabled,
        },
      })
      .pipe(switchMap(() => this.checkInsFacade.getCheckInTemplate$({ teamId: this.team.id })))
      .subscribe((data) => {
        this.checkInsDataService.setCheckInTemplateSettingsData({ settings: null, template: data });
        this.questions = this.questions.map((q) => {
          if (q.id === question.id) {
            q = { ...q, ...question };
          }
          return q;
        });
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        this.cdr.markForCheck();
      });
  }

  public deleteQuestion(params: { questionId: string; index: number }): void {
    this.checkInsFacade
      .deleteCheckInQuestion$({ templateId: this.checkInTemplate.id, questionId: params.questionId })
      .pipe(switchMap(() => this.checkInsFacade.getCheckInTemplate$({ teamId: this.team.id })))
      .subscribe((data) => {
        this.checkInsDataService.setCheckInTemplateSettingsData({ settings: null, template: data });
        (this.formGroup.controls.questions as FormArray).removeAt(params.index);
        this.questions = this.questions.filter((q) => q.id !== params.questionId);
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        this.cdr.markForCheck();
      });
  }

  public updateEditMode(params: { status: boolean }): void {
    this.isEditModeEnabled = params.status;
    this.showQuestionInput = false;
  }

  public enableQuestionEditMode(questionId: string): void {
    let updatedQuestions = this.getQuestions.getRawValue();
    updatedQuestions = updatedQuestions.map((question) => ({ isEditModeEnabled: question.id === questionId }));
    this.getQuestions.patchValue(updatedQuestions);
  }

  public disableQuestionsEditMode(): void {
    let updatedQuestions = this.getQuestions.getRawValue();
    updatedQuestions = updatedQuestions.map(() => ({ isEditModeEnabled: false }));
    this.getQuestions.patchValue(updatedQuestions);
  }

  public toggleCustomQuestion(): void {
    this.showQuestionInput = !this.showQuestionInput;
    this.newQuestionTitle = "";
    this.questionLength = 0;
  }

  public addCustomQuestion(): void {
    if (!this.newQuestionTitle) {
      return;
    }

    this.showQuestionInput = false;
    const newQuestion = { text: this.newQuestionTitle };

    this.checkInsFacade
      .postCheckInQuestion$({ templateId: this.checkInTemplate.id, payload: newQuestion })
      .pipe(
        switchMap((question) => {
          return this.checkInsFacade.getCheckInTemplate$({ teamId: this.team.id }).pipe(
            map((template) => {
              return { template, question };
            })
          );
        })
      )
      .subscribe((data) => {
        this.checkInsDataService.setCheckInTemplateSettingsData({ settings: null, template: data.template });
        this.addFormQuestion({ ...data.question, isEditModeEnabled: false });
        this.questions = [...this.questions, data.question];
        this.showQuestionInput = false;
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        this.cdr.markForCheck();
      });

    this.newQuestionTitle = "";
  }

  public onToggleCheckIns(reflectionsEnabled: boolean): void {
    this.checkInsFacade
      .patchCheckInSettings$({ payload: { reflectionsEnabled }, settingsId: this.checkInSettings.id })
      .pipe(untilDestroyed(this))
      .subscribe((checkInSettings: CheckInSettingsDTO) => {
        this.checkInsDataService.setCheckInTemplateSettingsData({ settings: checkInSettings, template: null });
        this.checkInSettings = checkInSettings;
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        this.cdr.markForCheck();
      });
  }

  public onToggleCheckInMood(moodsEnabled: boolean): void {
    this.onToggleCheckInMoodAndNote({ moodsEnabled, notesEnabled: this.checkInTemplate.notesEnabled });
  }

  public onToggleCheckInNote(notesEnabled: boolean): void {
    this.onToggleCheckInMoodAndNote({ moodsEnabled: this.checkInTemplate.moodsEnabled, notesEnabled });
  }

  private onToggleCheckInMoodAndNote(params: { moodsEnabled: boolean; notesEnabled: boolean }): void {
    this.checkInsFacade
      .patchCheckInTemplate$({ payload: { moodsEnabled: params.moodsEnabled, notesEnabled: params.notesEnabled }, templateId: this.checkInTemplate.id })
      .pipe(untilDestroyed(this))
      .subscribe((checkInTemplate: CheckInTemplateDTO) => {
        this.checkInTemplate = checkInTemplate;
        this.checkInsDataService.setCheckInTemplateSettingsData({ settings: null, template: checkInTemplate });
        this.isCheckInFormEmptyAlertEnabled = this.calculateCheckInFormEmptyStatus();
        this.cdr.markForCheck();
      });
  }

  public calculateCheckInFormEmptyStatus(): boolean {
    return !this.checkInTemplate.notesEnabled && this.questions.every((question) => !question.isEnabled);
  }

  public onKeydown(): void {
    window.setTimeoutOutsideAngular(() => {
      const activeTab = this.elementRef.nativeElement.querySelector(".ant-tabs-tabpane-active");

      if (activeTab) {
        activeTab.setAttribute("tabindex", "-1");
      }
    });
  }
}
